/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  LayoutGrid,
  List,
  ShoppingCart,
  Filter,
  ChevronDown,
  X,
  SlidersHorizontal,
  Heart,
  Package,
  Loader2,
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import { productService } from '../api/products';
import { getCategories } from '../api';
import { fetchSubCategoryLabels } from '../utils/subCategory';
import { usePricingRefresh } from '../hooks/usePricingRefresh';
import { Product } from '../types/admin';
import { formatINR } from '../utils/currency';
import { getProductSellingPrice } from '../utils/pricing';
import { getFullImageUrl } from '../utils/imageUtils';

/* ============================================================
   ANIMATION VARIANTS
   ============================================================ */
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } }
};

/* ============================================================
   HERO SECTION
   ============================================================ */
const HeroSection: React.FC<{ totalProducts: number }> = ({ totalProducts }) => (
  <section className="relative overflow-hidden bg-slate-900">
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <motion.div
        animate={{ x: [0, 60, 0], y: [0, -30, 0], scale: [1, 1.2, 1] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-blue-600/25 to-purple-600/15 blur-3xl"
      />
      <motion.div
        animate={{ x: [0, -40, 0], y: [0, 40, 0], scale: [1, 1.1, 1] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-gradient-to-tl from-emerald-500/15 to-cyan-500/15 blur-3xl"
      />
      <motion.div
        animate={{ x: [0, 30, 0], y: [0, 50, 0], scale: [1, 1.15, 1] }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut', delay: 4 }}
        className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-gradient-to-br from-amber-500/10 to-rose-500/10 blur-3xl"
      />
    </div>

    <div className="absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
      backgroundSize: '32px 32px'
    }} />

    <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="py-10 md:py-14">
        {/* Breadcrumb */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex items-center gap-2 text-sm text-white/50 mb-6"
        >
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-white font-medium">All Products</span>
        </motion.div>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-5">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-3 py-1 text-xs text-white/80">
                <Package className="w-3 h-3 text-amber-400" />
                Store
              </span>
              {totalProducts > 0 && (
                <span className="inline-flex items-center gap-1 bg-emerald-500/20 backdrop-blur-md border border-emerald-500/30 rounded-full px-3 py-1 text-xs text-emerald-300">
                  <Sparkles className="w-3 h-3" />
                  {totalProducts} products
                </span>
              )}
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              All <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Products</span>
            </h1>
            <p className="text-white/50 text-sm md:text-base max-w-xl mt-3 leading-relaxed">
              Discover our curated collection of premium products. Quality you can trust, prices you'll love.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="hidden md:block"
          >
            <Link
              to="/categories"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-5 py-2.5 text-sm text-white/80 hover:bg-white/20 hover:text-white transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              Browse Categories
            </Link>
          </motion.div>
        </div>
      </div>
    </div>

    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-50 to-transparent" />
  </section>
);

/* ============================================================
   STICKY FILTER BAR
   ============================================================ */
type StoreCategory = { _id: string; name: string; slug: string };

const FilterBar: React.FC<{
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  selectedCategory: string;
  setSelectedCategory: (v: string) => void;
  categories: StoreCategory[];
  selectedSubCategory: string;
  setSelectedSubCategory: (v: string) => void;
  subCategoryOptions: string[];
  sortBy: string;
  setSortBy: (v: string) => void;
  viewMode: 'grid' | 'list';
  setViewMode: (v: 'grid' | 'list') => void;
  totalProducts: number;
  visibleCount: number;
}> = ({
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  categories,
  selectedSubCategory,
  setSelectedSubCategory,
  subCategoryOptions,
  sortBy,
  setSortBy,
  viewMode,
  setViewMode,
  totalProducts,
  visibleCount,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="sticky top-0 z-40 bg-gray-50/95 backdrop-blur-md border-b border-gray-200/80">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row gap-3 py-3 items-stretch sm:items-center">
          {/* Search */}
          <div className={`relative flex-1 max-w-md transition-all duration-300 ${isFocused ? 'transform scale-[1.01]' : ''}`}>
            <div className={`absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl blur opacity-0 transition-opacity duration-300 ${isFocused ? 'opacity-20' : ''}`} />
            <div className="relative flex items-center bg-white border border-gray-200 rounded-xl overflow-hidden focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
              <Search className="w-4 h-4 text-gray-400 ml-4 flex-shrink-0" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                className="flex-1 bg-transparent border-none outline-none px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="mr-2 p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 hidden lg:inline">
              {visibleCount} of {totalProducts}
            </span>
            <div className="h-6 w-px bg-gray-300 hidden lg:block" />

            {/* Category */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="appearance-none bg-white border border-gray-200 rounded-xl pl-9 pr-8 py-2.5 text-sm text-gray-700 cursor-pointer hover:border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat.slug}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            </div>

            <select
              value={selectedSubCategory}
              onChange={(e) => setSelectedSubCategory(e.target.value)}
              disabled={!selectedCategory}
              className="appearance-none bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 cursor-pointer hover:border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 min-w-[150px] disabled:bg-gray-100 disabled:text-gray-400"
              aria-label="Filter by sub-category"
            >
              <option value="">
                {selectedCategory ? 'All sub-categories' : 'Select category first'}
              </option>
              {subCategoryOptions.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </select>

            {/* Sort */}
            <div className="relative">
              <SlidersHorizontal className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none bg-white border border-gray-200 rounded-xl pl-9 pr-8 py-2.5 text-sm text-gray-700 cursor-pointer hover:border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
              >
                <option value="createdAt">Newest</option>
                <option value="price">Price: Low to High</option>
                <option value="-price">Price: High to Low</option>
                <option value="name">Name: A-Z</option>
                <option value="-name">Name: Z-A</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            </div>

            {/* View Mode */}
            <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2.5 transition-colors ${viewMode === 'grid' ? 'bg-slate-900 text-white' : 'text-gray-500 hover:text-gray-700'}`}
                title="Grid view"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2.5 transition-colors ${viewMode === 'list' ? 'bg-slate-900 text-white' : 'text-gray-500 hover:text-gray-700'}`}
                title="List view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Active Filters */}
        {(searchTerm || selectedCategory) && (
          <div className="flex items-center gap-2 pb-3 flex-wrap">
            {searchTerm && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 border border-blue-100 text-blue-700 text-xs rounded-full">
                Search: {searchTerm}
                <button onClick={() => setSearchTerm('')} className="hover:text-blue-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {selectedCategory && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-50 border border-purple-100 text-purple-700 text-xs rounded-full">
                {selectedCategory}
                <button onClick={() => setSelectedCategory('')} className="hover:text-purple-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ============================================================
   SKELETON LOADING
   ============================================================ */
const SkeletonCard = () => (
  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
    <div className="aspect-square bg-gray-200" />
    <div className="p-4">
      <div className="h-3 bg-gray-200 rounded w-1/3 mb-2" />
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
      <div className="h-5 bg-gray-200 rounded w-1/3 mb-3" />
      <div className="h-10 bg-gray-200 rounded-xl" />
    </div>
  </div>
);

const LoadingState = () => (
  <div className="min-h-screen bg-gray-50">
    <div className="bg-slate-900 py-12 md:py-16">
      <div className="container mx-auto px-4">
        <div className="animate-pulse">
          <div className="h-4 bg-white/10 rounded-full w-48 mb-6" />
          <div className="h-10 bg-white/10 rounded-lg w-72 mb-2" />
          <div className="h-5 bg-white/10 rounded w-96" />
        </div>
      </div>
    </div>
    <div className="container mx-auto px-4 py-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
        {Array.from({ length: 10 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    </div>
  </div>
);

/* ============================================================
   EMPTY STATE
   ============================================================ */
const EmptyState: React.FC<{ onClear: () => void }> = ({ onClear }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4 }}
    className="flex flex-col items-center justify-center py-20 text-center"
  >
    <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-slate-100 to-gray-200 flex items-center justify-center mb-6">
      <Search className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-2xl font-bold text-gray-900 mb-2">No products found</h3>
    <p className="text-gray-500 mb-8 max-w-md">
      Try adjusting your search or filters to find what you're looking for.
    </p>
    <button
      onClick={onClear}
      className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-medium hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20"
    >
      <X className="w-4 h-4" />
      Clear Filters
    </button>
  </motion.div>
);

/* ============================================================
   ERROR STATE
   ============================================================ */
const ErrorState: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center max-w-md"
    >
      <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center mx-auto mb-6 border border-red-100">
        <X className="w-10 h-10 text-red-400" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-3">Failed to load products</h2>
      <p className="text-gray-500 mb-8">Something went wrong while fetching the products.</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-medium hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20"
      >
        <Loader2 className="w-4 h-4" />
        Try Again
      </button>
    </motion.div>
  </div>
);

/* ============================================================
   PRODUCT CARD — GRID
   ============================================================ */
const GridCard: React.FC<{
  product: Product;
  onClick: () => void;
}> = ({ product, onClick }) => {
  const navigate = useNavigate();
  const [imgSrc, setImgSrc] = useState(getProductImage(product));

  const price = getPrice(product);
  const originalPrice = getOriginalPrice(product);
  const discount = getDiscount(product);

  const rating = typeof product.rating === 'object' ? product.rating?.average : product.rating;
  const ratingCount = typeof product.rating === 'object' ? product.rating?.count : 0;

  const isOutOfStock = !product.inventory?.stock || product.inventory.stock <= 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-20px' }}
      transition={{ duration: 0.4 }}
      className="group bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-xl hover:shadow-gray-200/50 overflow-hidden transition-all duration-300 cursor-pointer"
      onClick={onClick}
    >
      <div className="relative aspect-square overflow-hidden bg-gray-50">
        <img
          src={imgSrc}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          onError={() => setImgSrc('/placeholder.svg')}
        />

        {/* Discount Badge */}
        {discount > 0 && (
          <span className="absolute top-3 left-3 bg-gradient-to-r from-red-500 to-rose-500 text-white text-[11px] font-bold px-2.5 py-1 rounded-lg shadow-md shadow-red-500/20">
            -{discount}%
          </span>
        )}
        {product.featured && (
          <span className="absolute top-3 right-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-[11px] font-bold px-2.5 py-1 rounded-lg shadow-md shadow-amber-400/20">
            Featured
          </span>
        )}

        {/* Wishlist Button */}
        <button
          onClick={(e) => e.stopPropagation()}
          className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-md text-gray-400 hover:text-red-500 hover:bg-red-50"
        >
          <Heart className="w-3.5 h-3.5" />
        </button>

        {/* Quick Add */}
        <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <button
            onClick={(e) => e.stopPropagation()}
            disabled={isOutOfStock}
            className={`w-full font-medium py-2.5 rounded-xl shadow-lg flex items-center justify-center gap-2 transition-colors text-sm ${
              isOutOfStock
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-slate-900 text-white hover:bg-slate-800'
            }`}
          >
            <ShoppingCart className="w-4 h-4" />
            {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
          </button>
        </div>
      </div>

      <div className="p-4">
        <p className="text-[11px] font-semibold text-blue-600 uppercase tracking-wider mb-1.5">
          {typeof product.category === 'object' ? product.category?.name : product.category}
        </p>
        <h3 className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2 min-h-[2.5rem] group-hover:text-blue-600 transition-colors">
          {product.name}
        </h3>
        {Number(rating) > 0 && ratingCount > 0 && (
          <div className="flex items-center gap-1.5 mt-2">
            <div className="flex items-center bg-[#00A650] text-white px-1.5 py-0.5 rounded text-xs font-medium">
              <span>{Number(rating).toFixed(1)}</span>
              <span className="ml-0.5">★</span>
            </div>
            <span className="text-xs text-gray-400">({ratingCount})</span>
          </div>
        )}
        <div className="mt-2.5 flex items-baseline gap-2">
          <span className="text-lg font-bold text-gray-900">{formatINR(price)}</span>
          {originalPrice && originalPrice > price && (
            <span className="text-sm text-gray-400 line-through">{formatINR(originalPrice)}</span>
          )}
        </div>
        <div className="mt-2 flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${isOutOfStock ? 'bg-red-400' : 'bg-emerald-400'}`} />
          <span className="text-xs text-gray-500">{isOutOfStock ? 'Out of Stock' : 'In Stock'}</span>
        </div>
      </div>
    </motion.div>
  );
};

/* ============================================================
   PRODUCT CARD — LIST
   ============================================================ */
const ListCard: React.FC<{
  product: Product;
  onClick: () => void;
}> = ({ product, onClick }) => {
  const [imgSrc, setImgSrc] = useState(getProductImage(product));

  const price = getPrice(product);
  const originalPrice = getOriginalPrice(product);
  const discount = getDiscount(product);

  const rating = typeof product.rating === 'object' ? product.rating?.average : product.rating;
  const ratingCount = typeof product.rating === 'object' ? product.rating?.count : 0;
  const isOutOfStock = !product.inventory?.stock || product.inventory.stock <= 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-10px' }}
      transition={{ duration: 0.3 }}
      className="group bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg overflow-hidden transition-all duration-300 cursor-pointer"
      onClick={onClick}
    >
      <div className="flex flex-col sm:flex-row">
        <div className="relative w-full sm:w-44 md:w-52 h-44 sm:h-40 flex-shrink-0 overflow-hidden bg-gray-50">
          <img
            src={imgSrc}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={() => setImgSrc('/placeholder.svg')}
          />
          {discount > 0 && (
            <span className="absolute top-2 left-2 bg-gradient-to-r from-red-500 to-rose-500 text-white text-[11px] font-bold px-2 py-1 rounded-lg">
              -{discount}%
            </span>
          )}
        </div>
        <div className="flex-1 p-4 flex flex-col justify-between">
          <div>
            <p className="text-[11px] font-semibold text-blue-600 uppercase tracking-wider mb-1">
              {typeof product.category === 'object' ? product.category?.name : product.category}
            </p>
            <h3 className="font-semibold text-gray-900 text-base group-hover:text-blue-600 transition-colors mb-2">
              {product.name}
            </h3>
            {(product.shortDescription || product.description) && (
              <p className="text-sm text-gray-500 line-clamp-2 mb-2">{product.shortDescription || product.description}</p>
            )}
            {Number(rating) > 0 && ratingCount > 0 && (
              <div className="flex items-center gap-1.5 mb-2">
                <div className="flex items-center bg-[#00A650] text-white px-1.5 py-0.5 rounded text-xs font-medium">
                  <span>{Number(rating).toFixed(1)}</span>
                  <span className="ml-0.5">★</span>
                </div>
                <span className="text-xs text-gray-400">({ratingCount})</span>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between mt-3 sm:mt-0">
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold text-gray-900">{formatINR(price)}</span>
              {originalPrice && originalPrice > price && (
                <span className="text-sm text-gray-400 line-through">{formatINR(originalPrice)}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => e.stopPropagation()}
                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
              >
                <Heart className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => e.stopPropagation()}
                disabled={isOutOfStock}
                className={`px-4 py-2 rounded-xl font-medium text-sm flex items-center gap-2 transition-colors ${
                  isOutOfStock
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-slate-900 text-white hover:bg-slate-800'
                }`}
              >
                <ShoppingCart className="w-4 h-4" />
                {isOutOfStock ? 'Out of Stock' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ============================================================
   BOTTOM CTA
   ============================================================ */
const BottomCTA: React.FC = () => (
  <section className="py-10 md:py-14 border-t border-gray-200/80">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-50px' }}
        variants={scaleIn}
        className="text-center"
      >
        <Link
          to="/categories"
          className="inline-flex items-center gap-2 text-slate-900 font-bold hover:text-blue-600 transition-colors text-base md:text-lg"
        >
          <ArrowLeft className="w-5 h-5" />
          Explore Categories
          <ArrowRight className="w-5 h-5" />
        </Link>
      </motion.div>
    </div>
  </section>
);

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
type ProductsPageProps = { variant?: 'default' | 'new-arrivals' };

const ProductsPage: React.FC<ProductsPageProps> = ({ variant = 'default' }) => {
  const navigate = useNavigate();
  const pricingRefresh = usePricingRefresh();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedSubCategory, setSelectedSubCategory] = useState(searchParams.get('subCategory') || '');
  const [subCategoryOptions, setSubCategoryOptions] = useState<string[]>([]);
  const [categories, setCategories] = useState<StoreCategory[]>([]);
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'createdAt');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalProducts, setTotalProducts] = useState(0);

  useEffect(() => {
    getCategories()
      .then((res) => {
        const list = res?.data ?? [];
        setCategories(
          Array.isArray(list)
            ? list.map((c: { _id: string; name: string; slug: string }) => ({
                _id: c._id,
                name: c.name,
                slug: c.slug,
              }))
            : []
        );
      })
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (!selectedCategory) {
      setSubCategoryOptions([]);
      return;
    }
    fetchSubCategoryLabels(selectedCategory).then(setSubCategoryOptions);
  }, [selectedCategory]);

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setSelectedSubCategory('');
  };

  // Fetch products
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await productService.getProducts({
        search: searchTerm.trim() || undefined,
        category: selectedCategory || undefined,
        ...(selectedSubCategory && selectedCategory ? { subCategory: selectedSubCategory } : {}),
        sort: sortBy,
        page: 1,
        limit: 24,
      });
      setProducts(response.data?.products || []);
      setTotalProducts(response.data?.pagination?.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCategory, selectedSubCategory, sortBy, pricingRefresh]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchProducts();
    }, searchTerm ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchProducts, searchTerm]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedSubCategory) params.set('subCategory', selectedSubCategory);
    if (sortBy) params.set('sort', sortBy);
    setSearchParams(params, { replace: true });
  }, [searchTerm, selectedCategory, selectedSubCategory, sortBy, setSearchParams]);

  const handleProductClick = (product: Product) => {
    const productSlug = product.slug || product._id;
    navigate(`/products/${productSlug}`);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedSubCategory('');
    setSortBy('createdAt');
  };

  const displayedProducts = products;

  if (loading) return <LoadingState />;
  if (error) return <ErrorState onRetry={fetchProducts} />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <HeroSection totalProducts={totalProducts} />

      {/* Sticky Filter Bar */}
      <FilterBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedCategory={selectedCategory}
        setSelectedCategory={handleCategoryChange}
        categories={categories}
        selectedSubCategory={selectedSubCategory}
        setSelectedSubCategory={setSelectedSubCategory}
        subCategoryOptions={subCategoryOptions}
        sortBy={sortBy}
        setSortBy={setSortBy}
        viewMode={viewMode}
        setViewMode={setViewMode}
        totalProducts={totalProducts}
        visibleCount={displayedProducts.length}
      />

      {/* Products */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {displayedProducts.length === 0 ? (
          <EmptyState onClear={clearFilters} />
        ) : (
          <>
            {/* Section Header */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="flex items-center gap-3 mb-6"
            >
              <div className="w-1.5 h-8 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full" />
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">Products</h2>
              <span className="text-sm text-gray-400 ml-2 hidden sm:inline">{displayedProducts.length} items</span>
            </motion.div>

            {/* Grid/List */}
            <div className={`
              ${viewMode === 'grid'
                ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5'
                : 'flex flex-col gap-4'
              }
            `}>
              {displayedProducts.map((product) =>
                viewMode === 'grid' ? (
                  <GridCard
                    key={product._id}
                    product={product}
                    onClick={() => handleProductClick(product)}
                  />
                ) : (
                  <ListCard
                    key={product._id}
                    product={product}
                    onClick={() => handleProductClick(product)}
                  />
                )
              )}
            </div>
          </>
        )}
      </div>

      {/* Bottom CTA */}
      <BottomCTA />
    </div>
  );
};

/* ============================================================
   HELPER FUNCTIONS
   ============================================================ */
function getProductImage(product: Product): string {
  if (product.images && Array.isArray(product.images) && product.images.length > 0) {
    const firstImage = product.images[0];
    if (typeof firstImage === 'string') {
      return getFullImageUrl(firstImage);
    } else if (firstImage && typeof firstImage === 'object' && 'url' in firstImage) {
      return getFullImageUrl(firstImage.url);
    }
  }
  return '/placeholder.svg';
}

function getPrice(product: Product): number {
  return getProductSellingPrice(product);
}

function getOriginalPrice(product: Product): number | null {
  if (typeof product.price === 'object') {
    return product.price.mrp || null;
  }
  return null;
}

function getDiscount(product: Product): number {
  const price = getPrice(product);
  const original = getOriginalPrice(product);
  if (original && original > price) {
    return Math.round(((original - price) / original) * 100);
  }
  return 0;
}

export default ProductsPage;
